// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useSchedulerScheduleStatusDailyReports from '../../hooks/useSchedulerScheduleStatusDailyReports';
import { formatScheduleStatusTooltipLabel } from '../../utils/schedulerScheduleStatusDailyChart';

const CHART_SECTION_HEIGHT_PX = 240;

function formatScheduleStatusTooltipFormatter(value, name) {
  return [Number(value).toLocaleString(), name];
}

function SchedulerScheduleStatusChart() {
  const theme = useTheme();
  const { chartRowsAscending, loading, error } = useSchedulerScheduleStatusDailyReports();
  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" gutterBottom component="div">
        Daily schedule status (UTC)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        From Firestore{' '}
        <code>{'schedulerScheduleStatusReports'}</code> · daily snapshot (active / paused / error suppressed)
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={32} />
        </Box>
      ) : null}
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unable to query scheduler schedule status reports. Confirm deployed scheduled reporter (<code>{'reportScheduleStatus'}</code>) and Firestore rules.
        </Alert>
      ) : null}
      {!loading && !error && chartRowsAscending.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No daily reports yet — the scheduled reporter writes one document per UTC day after the first run.
        </Typography>
      ) : null}
      {!loading && !error && chartRowsAscending.length > 0 ? (
        <Box sx={{ width: '100%', height: CHART_SECTION_HEIGHT_PX }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartRowsAscending}
              margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
            >
              <XAxis dataKey="dateLabelShort" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={40} />
              <Tooltip
                contentStyle={tooltipContentStyle}
                formatter={formatScheduleStatusTooltipFormatter}
                labelFormatter={formatScheduleStatusTooltipLabel}
              />
              <Legend wrapperStyle={{ fontSize: '0.85rem' }} />
              <Bar
                stackId="status"
                name="Active"
                dataKey="active"
                fill={theme.palette.success.main}
              />
              <Bar
                stackId="status"
                name="Paused"
                dataKey="paused"
                fill={theme.palette.warning.main}
              />
              <Bar
                stackId="status"
                name="Error Suppressed"
                dataKey="errorSuppressed"
                fill={theme.palette.error.main}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : null}
    </Box>
  );
}

export default SchedulerScheduleStatusChart;
